﻿import React, { useEffect, useMemo, useState } from 'react'
import { Heading, Text, Button, Input, Select } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import ModuleIconPickerModal from '../components/ModuleIconPickerModal'
import { modulesService } from '../services/modules'
import { Module, CreateModuleData, UpdateModuleData } from '../types'
import { BRAND_COLOR } from '../constants'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

const MODULE_NAME = 'configuracoes_modulo'

const defaultForm: CreateModuleData & { is_active?: boolean } = {
  name: '',
  display_name: '',
  description: '',
  icon: '',
  route: '',
  parent_module_id: undefined,
  order_index: 0,
  is_active: true
}

const ConfiguracoesModulos: React.FC = () => {
  const [modules, setModules] = useState<Module[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isIconPickerOpen, setIsIconPickerOpen] = useState(false)

  const [selectedModule, setSelectedModule] = useState<Module | null>(null)
  const [form, setForm] = useState<typeof defaultForm>(defaultForm)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Filtros
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [parentFilter, setParentFilter] = useState<string>('')

  const parentOptions = useMemo(() => modules.filter(m => !m.parent_module_id), [modules])
  const pageModule = useMemo(
    () => modules.find((m) => m.name === MODULE_NAME),
    [modules]
  )
  const moduleTitle = pageModule?.display_name || 'Configurações'
  const moduleDescription = pageModule?.description || 'Módulos'

  const loadModules = async () => {
    setLoading(true)
    setError(null)
    const { data, error: err } = await modulesService.getAll()
    if (err) setError(err)
    setModules(data || [])
    setLoading(false)
  }

  useEffect(() => {
    loadModules()
  }, [])

  const filteredModules = useMemo(() => {
    const term = search.trim().toLowerCase()
    return modules.filter((m) => {
      const matchesSearch = !term ||
        m.display_name.toLowerCase().includes(term) ||
        m.name.toLowerCase().includes(term) ||
        m.route.toLowerCase().includes(term)

      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && m.is_active) ||
        (statusFilter === 'inactive' && !m.is_active)

      const matchesParent = !parentFilter ||
        (parentFilter === 'none' && !m.parent_module_id) ||
        (m.parent_module_id === parentFilter)

      return matchesSearch && matchesStatus && matchesParent
    })
  }, [modules, search, statusFilter, parentFilter])

  const openCreate = () => {
    setForm(defaultForm)
    setIsCreateModalOpen(true)
  }

  const openEdit = (moduleToEdit: Module) => {
    setSelectedModule(moduleToEdit)
    setForm({
      name: moduleToEdit.name,
      display_name: moduleToEdit.display_name,
      description: moduleToEdit.description || '',
      icon: moduleToEdit.icon || '',
      route: moduleToEdit.route,
      parent_module_id: moduleToEdit.parent_module_id || undefined,
      order_index: moduleToEdit.order_index || 0,
      is_active: moduleToEdit.is_active
    })
    setIsEditModalOpen(true)
  }

  const openIconPicker = () => {
    setIsIconPickerOpen(true)
  }

  const selectIcon = (iconName: string) => {
    setForm({ ...form, icon: iconName })
    setIsIconPickerOpen(false)
  }

  

  const handleCreate = async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      const payload: CreateModuleData & { is_active?: boolean } = {
        name: form.name.trim(),
        display_name: form.display_name.trim(),
        description: form.description?.trim() || undefined,
        icon: form.icon?.trim() || undefined,
        route: form.route.trim(),
        parent_module_id: form.parent_module_id || undefined,
        order_index: parseFloat(String(form.order_index)) || 0,
        is_active: form.is_active
      }
      const { error: err } = await modulesService.create(payload)
      if (err) throw new Error(err)
      setIsCreateModalOpen(false)
      await loadModules()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEdit = async () => {
    if (!selectedModule) return
    setIsSubmitting(true)
    setError(null)
    try {
      const payload: UpdateModuleData & { is_active?: boolean } = {
        name: form.name.trim(),
        display_name: form.display_name.trim(),
        description: form.description?.trim() || undefined,
        icon: form.icon?.trim() || undefined,
        route: form.route.trim(),
        parent_module_id: form.parent_module_id || undefined,
        order_index: parseFloat(String(form.order_index)) || 0,
        is_active: form.is_active
      }
      const { error: err } = await modulesService.update(selectedModule.id, payload)
      if (err) throw new Error(err)
      setIsEditModalOpen(false)
      setSelectedModule(null)
      await loadModules()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (module: Module) => {
    if (!window.confirm(`Tem certeza que deseja excluir o módulo "${module.display_name}"?\n\nEsta ação não pode ser desfeita.`)) {
      return
    }

    setError(null)
    try {
      const { error: err } = await modulesService.delete(module.id)
      if (err) throw new Error(err)
      await loadModules()
    } catch (e) {
      setError((e as Error).message)
      alert(`Erro ao excluir módulo: ${(e as Error).message}`)
    }
  }

  

  return (
    <>
      <div className="h-full min-h-0 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
          <PermissionGuard 
            moduleName={MODULE_NAME}
            permissionType="show"
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                    Acesso Negado
                  </Text>
                  <Text size="sm" color="muted">
                    Você não tem permissão para acessar esta página.
                  </Text>
                </div>
              </div>
            }
          >
          <div className="px-6 py-6">
            <div className="flex items-start justify-between">
              <div>
                <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                  {moduleTitle}
                </Heading>
                <Text size="sm" color="muted" className="mt-1">
                  {moduleDescription}
                </Text>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={openCreate}
                  className="flex items-center gap-2 text-white"
                  style={{ backgroundColor: BRAND_COLOR }}
                >
                  <PlusIcon className="h-5 w-5" />
                  Novo Módulo
                </Button>
                <Button variant="ghost" onClick={loadModules}>Atualizar</Button>
              </div>
            </div>

            {/* Filtros */}
            <div className="mt-6">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="mb-4">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Filtros</Heading>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                  <div className="md:col-span-5">
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Busca</Text>
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Buscar por nome, interno ou rota"
                      className="w-full"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Status</Text>
                    <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)} className="w-full">
                      <option value="all">Todos</option>
                      <option value="active">Ativos</option>
                      <option value="inactive">Inativos</option>
                    </Select>
                  </div>
                  <div className="md:col-span-3">
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Módulo Pai</Text>
                    <Select value={parentFilter} onChange={(e) => setParentFilter(e.target.value)} className="w-full">
                      <option value="">Todos</option>
                      <option value="none">Sem pai</option>
                      {parentOptions.map((p) => (
                        <option key={p.id} value={p.id}>{p.display_name}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="md:col-span-1 flex justify-start">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSearch(''); setStatusFilter('all'); setParentFilter('') }}
                    >
                      Limpar
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Lista */}
            <div className="mt-6">
              {loading ? (
                <div className="text-gray-500 dark:text-gray-400">Carregando módulos...</div>
              ) : error ? (
                <div className="text-red-600 dark:text-red-400">{error}</div>
              ) : filteredModules.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300">Nenhum módulo cadastrado.</Text>
                  <Button onClick={openCreate} className="mt-4 text-white" style={{ backgroundColor: BRAND_COLOR }}>
                    <PlusIcon className="h-5 w-5 inline-block mr-2" /> Criar Primeiro Módulo
                  </Button>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-900/30">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-2/6">Módulo</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/6">Nome interno</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-2/6">Rota</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-16">Ordem</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/6 hidden lg:table-cell">Pai</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-24">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-24">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredModules.map(m => {
                          const parent = modules.find(x => x.id === m.parent_module_id)
                          return (
                            <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                              <td className="px-4 py-4">
                                <div className="flex flex-col">
                                  <Text size="sm" className="text-gray-900 dark:text-gray-100 font-medium truncate block max-w-[280px]">
                                    {m.display_name}
                                  </Text>
                                  <Text size="xs" className="text-gray-500 dark:text-gray-400 truncate block max-w-[320px]">
                                    {m.description ? (m.description.length > 30 ? m.description.substring(0, 30) + '...' : m.description) : 'Sem descrição'}
                                  </Text>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300 truncate max-w-[180px]">{m.name}</td>
                              <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300 truncate max-w-[260px]">{m.route}</td>
                              <td className="px-4 py-4 text-center text-sm text-gray-700 dark:text-gray-300">{m.order_index ?? 0}</td>
                              <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300 hidden lg:table-cell truncate max-w-[200px]">{parent ? parent.display_name : '-'}</td>
                              <td className="px-4 py-4 text-center whitespace-nowrap">
                                <span className={`text-xs font-medium px-2 py-1 rounded-full ${m.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-300'}`}>
                                  {m.is_active ? 'Ativo' : 'Inativo'}
                                </span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => openEdit(m)}
                                    title="Editar módulo"
                                    className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                  >
                                    <PencilIcon className="h-5 w-5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDelete(m)}
                                    title="Excluir módulo"
                                    className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                  >
                                    <TrashIcon className="h-5 w-5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
          </PermissionGuard>
      </div>

      {/* Modal Criar */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-xl">
            <div className="flex items-center justify-between mb-4">
              <Heading as="h3" size="2xl">Novo Módulo</Heading>
              <Button onClick={() => setIsCreateModalOpen(false)} variant="ghost">
                <XMarkIcon className="h-6 w-6" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nome interno</label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="ex: configuracoes_usuarios" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nome de exibição</label>
                <Input value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} placeholder="ex: Usuários" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Descrição</label>
                <Input value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Opcional" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Ícone</label>
                  <button
                    type="button"
                    onClick={() => openIconPicker()}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Ver ícones disponíveis
                  </button>
                </div>
                <Input value={form.icon || ''} onChange={e => setForm({ ...form, icon: e.target.value })} placeholder="ex: users" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rota</label>
                <Input value={form.route} onChange={e => setForm({ ...form, route: e.target.value })} placeholder="ex: /configuracoes-usuarios" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Módulo pai</label>
                <Select value={form.parent_module_id || ''} onChange={e => setForm({ ...form, parent_module_id: e.target.value || undefined })}>
                  <option value="">Sem pai</option>
                  {parentOptions.map(p => (
                    <option key={p.id} value={p.id}>{p.display_name}</option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ordem</label>
                <Input type="number" step="any" value={Number(form.order_index)} onChange={e => setForm({ ...form, order_index: parseFloat(e.target.value) })} />
              </div>
              <div className="flex items-center gap-2 md:col-span-2">
                <input id="create_is_active" type="checkbox" checked={!!form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
                <label htmlFor="create_is_active" className="text-sm text-gray-700 dark:text-gray-300">Módulo ativo</label>
              </div>
            </div>

            {error && <div className="text-red-600 dark:text-red-400 mt-4">{error}</div>}

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setIsCreateModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreate} className="text-white" style={{ backgroundColor: BRAND_COLOR }}>
                {isSubmitting ? 'Criando...' : 'Criar Módulo'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar */}
      {isEditModalOpen && selectedModule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-xl">
            <div className="flex items-center justify-between mb-4">
              <Heading as="h3" size="2xl">Editar Módulo</Heading>
              <Button onClick={() => setIsEditModalOpen(false)} variant="ghost">
                <XMarkIcon className="h-6 w-6" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nome interno</label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nome de exibição</label>
                <Input value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Descrição</label>
                <Input value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Ícone</label>
                  <button
                    type="button"
                    onClick={() => openIconPicker()}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Ver ícones disponíveis
                  </button>
                </div>
                <Input value={form.icon || ''} onChange={e => setForm({ ...form, icon: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rota</label>
                <Input value={form.route} onChange={e => setForm({ ...form, route: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Módulo pai</label>
                <Select value={form.parent_module_id || ''} onChange={e => setForm({ ...form, parent_module_id: e.target.value || undefined })}>
                  <option value="">Sem pai</option>
                  {parentOptions.map(p => (
                    <option key={p.id} value={p.id}>{p.display_name}</option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ordem</label>
                <Input type="number" step="any" value={Number(form.order_index)} onChange={e => setForm({ ...form, order_index: parseFloat(e.target.value) })} />
              </div>
              <div className="flex items-center gap-2 md:col-span-2">
                <input id="edit_is_active" type="checkbox" checked={!!form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
                <label htmlFor="edit_is_active" className="text-sm text-gray-700 dark:text-gray-300">Módulo ativo</label>
              </div>
            </div>

            {error && <div className="text-red-600 dark:text-red-400 mt-4">{error}</div>}

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setIsEditModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleEdit} className="text-white" style={{ backgroundColor: BRAND_COLOR }}>
                {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ModuleIconPickerModal
        open={isIconPickerOpen}
        selectedIcon={form.icon}
        onClose={() => setIsIconPickerOpen(false)}
        onSelect={selectIcon}
      />

      
    </>
  )
}

export default ConfiguracoesModulos


